import {
  DatabaseAuto, IbulkDelete,
  IbulkDeleteSuccessResponse, IdataArrayResponse,
  IdeleteOneResponse,
  IfilterProps,
  InonOperatingRevenueAnalytics, TbackEndGeneratedFields,
  TbackEndGeneratedFieldsExcludingId, validateData
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { Imanager, ImanagerNonOperatingRevenue } from '../../../shared-types';
import { ManagerClient } from '../../utils/manager-client';

/**
 * Type representing a manager nonOperatingRevenue request.
 */
export type TmanagerNonOperatingRevenueRequest<
  CreateMode extends boolean = false, U = Omit<
  Partial<ImanagerNonOperatingRevenue>,
  TbackEndGeneratedFieldsExcludingId | 'manager'>> = CreateMode extends true ? Omit<
  ImanagerNonOperatingRevenue | 'manager', TbackEndGeneratedFields> : U;


/**
 * Type representing a manager nonOperatingRevenue.
 */
type TmanagerNonOperatingRevenueProps = ImanagerNonOperatingRevenue<number, Imanager>;

export class ManagerNonOperatingRevenue
  extends DatabaseAuto {
  /**
     * The base URL for the manager nonOperatingRevenue.
     */
  private static readonly baseUrl = '/manager-non-operating-revenue';

  /**
   * The unique reference ID.
   */
  readonly urId: TmanagerNonOperatingRevenueProps['urId'];

  /**
   * The name of the nonOperatingRevenue.
   */
  readonly name: TmanagerNonOperatingRevenueProps['name'];

  /**
   * The manager of the nonOperatingRevenue.
   */
  readonly manager: TmanagerNonOperatingRevenueProps['manager'];

  /**
   * The amount of the nonOperatingRevenue.
   */
  readonly amount: TmanagerNonOperatingRevenueProps['amount'];

  /**
   * The note of the nonOperatingRevenue.
   */
  readonly note: string;


  /**
   * Constructs an instance of `ManagerNonOperatingRevenue`.
   *
   * @param {TmanagerNonOperatingRevenueProps} data - The nonOperatingRevenue data used to initialize the instance.
   * @throws {Error} Throws an error if no data is provided.
   */
  constructor(data: TmanagerNonOperatingRevenueProps) {
    validateData(data, {
      urId: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Unique reference ID must be a non-empty string'
      },
      name: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'ManagerNonOperatingRevenue name must be a non-empty string'
      },
      amount: {
        validate: (v) =>
          typeof v === 'number' &&
          v >= 0,
        message: 'Cost must be a non-negative number'
      },
      manager: {
        validate: (v) =>
          typeof v === 'object' &&
          v._id.trim().length > 0,
        message: 'Added by name must be a valid user object'
      }
    });

    super(data);

    this.urId = data.urId;
    this.name = data.name;
    this.manager = data.manager;
    this.amount = data.amount;
    this.note = data.note || '';
  }

  /**
   * Retrieves nonOperatingRevenue analytics based on the provided filter.
   *
   * @param {IfilterProps} filter - The filter criteria for retrieving nonOperatingRevenue analytics.
   * @returns {Promise<InonOperatingRevenueAnalytics>} The result of the analytics operation.
   */
  static getNonOperatingRevenueAnalytics(filter: IfilterProps) {
    const observer$ = ManagerClient.ehttp
      .makePost<InonOperatingRevenueAnalytics>(`${ManagerNonOperatingRevenue.baseUrl}/analytics`, filter);

    return lastValueFrom(observer$);
  }

  /**
   * Retrieves all nonOperatingRevenues with pagination support.
   *
   * @param {number} [offset=0] - The starting point for pagination.
   * @param {number} [limit=20] - The number of items to retrieve.
   * @returns {Promise<{ count: number, dataArr: ManagerNonOperatingRevenue[] }>} An object containing
   * the total count and an array of `ManagerNonOperatingRevenue` instances.
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();
    const observer$ = ManagerClient.ehttp
      .makeGet<IdataArrayResponse<TmanagerNonOperatingRevenueProps>>(`${ManagerNonOperatingRevenue.baseUrl}?${query}`);
    const nonOperatingRevenues = await lastValueFrom(observer$);

    return {
      count: nonOperatingRevenues.count,
      dataArr: nonOperatingRevenues.data.map((val) => new ManagerNonOperatingRevenue(val))
    };
  }

  /**
   * Filters nonOperatingRevenues based on the provided filter properties.
   *
   * @param {IfilterProps} filter - The filter criteria for retrieving nonOperatingRevenues.
   * @returns {Promise<{ count: number, dataArr: ManagerNonOperatingRevenue[] }>} An object containing
   * the total count and an array of `ManagerNonOperatingRevenue` instances.
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = ManagerClient.ehttp
      .makePost<IdataArrayResponse<TmanagerNonOperatingRevenueProps>>(
        `${ManagerNonOperatingRevenue.baseUrl}/filter`,
        filter
      );
    const nonOperatingRevenues = await lastValueFrom(observer$);

    return {
      count: nonOperatingRevenues.count,
      dataArr: nonOperatingRevenues.data.map((val) => new ManagerNonOperatingRevenue(val))
    };
  }

  /**
   * Retrieves a single nonOperatingRevenue based on the provided resource ID.
   *
   * @param {string} resourceId - The unique identifier for the nonOperatingRevenue to retrieve.
   * @returns {Promise<ManagerNonOperatingRevenue>} The `ManagerNonOperatingRevenue`
   * instance corresponding to the provided resource ID.
   */
  static async getOne(resourceId: string) {
    const observer$ = ManagerClient.ehttp
      .makeGet<TmanagerNonOperatingRevenueProps>(`${ManagerNonOperatingRevenue.baseUrl}/${resourceId}`);
    const nonOperatingRevenue = await lastValueFrom(observer$);

    return new ManagerNonOperatingRevenue(nonOperatingRevenue);
  }

  /**
   * Adds a new nonOperatingRevenue.
   *
   * @param {InonOperatingRevenue} payload - The nonOperatingRevenue data to add.
   * @returns {Promise<ManagerNonOperatingRevenue>} The result of the addition operation.
   */
  static async add(payload: TmanagerNonOperatingRevenueRequest<true>) {
    const observer$ = ManagerClient.ehttp
      .makePost<TmanagerNonOperatingRevenueProps>(`${ManagerNonOperatingRevenue.baseUrl}`, payload);

    return new ManagerNonOperatingRevenue(await lastValueFrom(observer$));
  }

  /**
   * Removes multiple nonOperatingRevenues based on the provided criteria.
   *
   * @param {IbulkDelete} payload - The criteria for deleting multiple nonOperatingRevenues.
   * @returns {Promise<IbulkDeleteSuccessResponse>} The result of the deletion operation.
   */
  static bulkDelete(payload: IbulkDelete) {
    const observer$ = ManagerClient.ehttp
      .makePost<IbulkDeleteSuccessResponse>(`${ManagerNonOperatingRevenue.baseUrl}/bulk-delete`, payload);

    return lastValueFrom(observer$);
  }

  /**
   * Updates the current nonOperatingRevenue with the provided data.
   *
   * @param {InonOperatingRevenue} payload - The updated nonOperatingRevenue data.
   * @returns {Promise<ManagerNonOperatingRevenue>} The result of the update operation.
   */
  async update(payload: TmanagerNonOperatingRevenueRequest) {
    const observer$ = ManagerClient.ehttp
      .makePut<TmanagerNonOperatingRevenueProps>(`${ManagerNonOperatingRevenue.baseUrl}/${this._id}`, payload);

    return new ManagerNonOperatingRevenue(await lastValueFrom(observer$));
  }

  /**
   * Removes the current nonOperatingRevenue.
   *
   * @returns {Promise<IdeleteOneResponse | null>} The result of the removal operation.
   */
  remove() {
    const observer$ = ManagerClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${ManagerNonOperatingRevenue.baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }
}
